import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { api, clearTokens, platformLogin, restoreSession, storeTokens } from "./api";
import type { PlatformUser } from "./types";

interface AuthContextValue {
  user: PlatformUser | null;
  loading: boolean;
  login(email: string, password: string, mfaCode?: string): Promise<void>;
  refreshUser(): Promise<PlatformUser>;
  logout(): Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<PlatformUser | null>(null);
  const [loading, setLoading] = useState(true);

  const loadUser = useCallback(async (): Promise<PlatformUser> => {
    const current = await api<PlatformUser>("/me");
    if (!current.platformAdmin) throw new Error("Esta cuenta no es administradora de plataforma.");
    setUser(current);
    return current;
  }, []);

  useEffect(() => {
    void (async () => {
      try {
        if (!await restoreSession()) return;
        await loadUser();
      } catch {
        clearTokens();
      } finally {
        setLoading(false);
      }
    })();
  }, [loadUser]);

  const value = useMemo<AuthContextValue>(() => ({
    user,
    loading,
    async login(email, password, mfaCode) {
      const tokens = await platformLogin(email, password, mfaCode);
      if (!tokens.user.platformAdmin) {
        clearTokens();
        throw new Error("Esta consola está reservada a administradores de Cochegest.");
      }
      storeTokens(tokens);
      setUser(tokens.user);
    },
    refreshUser: loadUser,
    async logout() {
      try { await api<void>("/auth/logout", { method: "POST" }); } catch { /* local logout remains available */ }
      clearTokens();
      setUser(null);
    },
  }), [loadUser, loading, user]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth requiere AuthProvider");
  return value;
}
